import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  FiCalendar,
  FiFilter,
  FiRefreshCw,
  FiSearch,
  FiX,
} from "react-icons/fi";

import "./QuotationFilter.css";

/*
|--------------------------------------------------------------------------
| DEFAULT FILTERS
|--------------------------------------------------------------------------
*/

const DEFAULT_FILTERS = {
  search: "",
  documentStatus: "",
  conversionStatus: "",
  salesChannel: "",
  sourceType: "",

  customerId: "",
  salespersonId: "",
  branchId: "",
  firmId: "",
  warehouseId: "",

  fromDate: "",
  toDate: "",

  validUntilFrom: "",
  validUntilTo: "",

  sortBy: "documentDate",
  sortOrder: "desc",

  page: 1,
  limit: 10,
};

/*
|--------------------------------------------------------------------------
| HELPERS
|--------------------------------------------------------------------------
*/

const formatOptionLabel = (
  value
) => {
  if (!value) {
    return "";
  }

  return String(value)
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(
      /\b\w/g,
      (character) =>
        character.toUpperCase()
    );
};

const normalizeOptions = (
  options = []
) => {
  if (!Array.isArray(options)) {
    return [];
  }

  return options.map(
    (
      option,
      index
    ) => {
      if (
        typeof option ===
        "string"
      ) {
        return {
          key: `${option}-${index}`,
          label:
            formatOptionLabel(
              option
            ),
          value:
            option,
        };
      }

      const value =
        option?.value ??
        option?._id ??
        option?.id ??
        option?.key ??
        "";

      const label =
        option?.label ??
        option?.name ??
        option?.title ??
        option?.customerName ??
        option?.employeeName ??
        option?.branchName ??
        option?.firmName ??
        option?.warehouseName ??
        formatOptionLabel(
          value
        );

      return {
        ...option,
        key:
          option?.key ||
          value ||
          index,
        label,
        value,
      };
    }
  );
};

const getActiveFilterCount = (
  filters
) => {
  const ignoredKeys = [
    "page",
    "limit",
    "sortBy",
    "sortOrder",
  ];

  return Object.entries(
    filters || {}
  ).filter(
    ([key, value]) =>
      !ignoredKeys.includes(
        key
      ) &&
      value !== "" &&
      value !== null &&
      value !== undefined &&
      value !== false
  ).length;
};

/*
|--------------------------------------------------------------------------
| QUOTATION FILTER
|--------------------------------------------------------------------------
*/

const QuotationFilter = ({
  filters = {},

  dropdowns = {},

  customerOptions = [],
  salespersonOptions = [],
  branchOptions = [],
  firmOptions = [],
  warehouseOptions = [],

  loading = false,

  onChange,
  onApply,
  onReset,
}) => {
  const [
    showAdvancedFilters,
    setShowAdvancedFilters,
  ] = useState(false);

  const [
    localFilters,
    setLocalFilters,
  ] = useState({
    ...DEFAULT_FILTERS,
    ...filters,
  });

  /*
  |--------------------------------------------------------------------------
  | SYNC EXTERNAL FILTERS
  |--------------------------------------------------------------------------
  */

  useEffect(() => {
    setLocalFilters({
      ...DEFAULT_FILTERS,
      ...(filters || {}),
    });
  }, [filters]);

  /*
  |--------------------------------------------------------------------------
  | OPTIONS
  |--------------------------------------------------------------------------
  */

  const documentStatusOptions =
    useMemo(
      () =>
        normalizeOptions(
          dropdowns?.documentStatuses ||
            [
              "DRAFT",
              "CONFIRMED",
              "CANCELLED",
            ]
        ),
      [
        dropdowns
          ?.documentStatuses,
      ]
    );

  const conversionStatusOptions =
    useMemo(
      () =>
        normalizeOptions(
          dropdowns?.conversionStatuses ||
            [
              "NOT_CONVERTED",
              "PARTIALLY_CONVERTED",
              "CONVERTED",
            ]
        ),
      [
        dropdowns
          ?.conversionStatuses,
      ]
    );

  const salesChannelOptions =
    useMemo(
      () =>
        normalizeOptions(
          dropdowns?.salesChannels ||
            [
              "STORE",
              "ONLINE",
              "WHATSAPP",
              "PHONE",
              "POS",
              "OTHER",
            ]
        ),
      [
        dropdowns
          ?.salesChannels,
      ]
    );

  const sourceTypeOptions =
    useMemo(
      () =>
        normalizeOptions(
          dropdowns?.sourceTypes ||
            []
        ),
      [
        dropdowns
          ?.sourceTypes,
      ]
    );

  const normalizedCustomerOptions =
    useMemo(
      () =>
        normalizeOptions(
          customerOptions
        ),
      [customerOptions]
    );

  const normalizedSalespersonOptions =
    useMemo(
      () =>
        normalizeOptions(
          salespersonOptions
        ),
      [salespersonOptions]
    );

  const normalizedBranchOptions =
    useMemo(
      () =>
        normalizeOptions(
          branchOptions
        ),
      [branchOptions]
    );

  const normalizedFirmOptions =
    useMemo(
      () =>
        normalizeOptions(
          firmOptions
        ),
      [firmOptions]
    );

  const normalizedWarehouseOptions =
    useMemo(
      () =>
        normalizeOptions(
          warehouseOptions
        ),
      [warehouseOptions]
    );

  const sortByOptions =
    [
      {
        value:
          "documentDate",
        label:
          "Quotation Date",
      },
      {
        value:
          "validUntil",
        label:
          "Valid Until",
      },
      {
        value:
          "documentNumber",
        label:
          "Quotation Number",
      },
      {
        value:
          "grandTotal",
        label:
          "Grand Total",
      },
      {
        value:
          "createdAt",
        label:
          "Created Date",
      },
    ];

  const sortOrderOptions =
    [
      {
        value:
          "desc",
        label:
          "Descending",
      },
      {
        value:
          "asc",
        label:
          "Ascending",
      },
    ];

  const activeFilterCount =
    getActiveFilterCount(
      localFilters
    );

  /*
  |--------------------------------------------------------------------------
  | HANDLERS
  |--------------------------------------------------------------------------
  */

  const updateFilter = (
    key,
    value
  ) => {
    const updatedFilters = {
      ...localFilters,
      [key]:
        value,
    };

    if (
      key !== "page" &&
      key !== "limit"
    ) {
      updatedFilters.page = 1;
    }

    setLocalFilters(
      updatedFilters
    );

    onChange?.(
      key,
      value,
      updatedFilters
    );
  };

  const handleApply = () => {
    onApply?.({
      ...localFilters,
      page: 1,
    });
  };

  const handleReset = () => {
    const resetFilters = {
      ...DEFAULT_FILTERS,
      limit:
        Number(
          filters?.limit
        ) || 10,
    };

    setLocalFilters(
      resetFilters
    );

    setShowAdvancedFilters(
      false
    );

    onReset?.(
      resetFilters
    );
  };

  const handleSearchKeyDown = (
    event
  ) => {
    if (
      event.key ===
      "Enter"
    ) {
      handleApply();
    }
  };

  const clearSearch = () => {
    updateFilter(
      "search",
      ""
    );
  };

  const renderSelect = ({
    key,
    label,
    options,
    placeholder,
  }) => (
    <div className="quotation-filter-field">
      <label
        htmlFor={`quotation-filter-${key}`}
      >
        {label}
      </label>

      <select
        id={`quotation-filter-${key}`}
        value={
          localFilters?.[
            key
          ] || ""
        }
        onChange={(
          event
        ) =>
          updateFilter(
            key,
            event.target
              .value
          )
        }
      >
        <option value="">
          {placeholder ||
            `All ${label}`}
        </option>

        {options.map(
          (
            option,
            index
          ) => (
            <option
              key={
                option.key ||
                option.value ||
                index
              }
              value={
                option.value
              }
            >
              {option.label}
            </option>
          )
        )}
      </select>
    </div>
  );

  return (
    <div className="quotation-filter-card">
      <div className="quotation-filter-main">
        <div className="quotation-filter-search">
          <FiSearch />

          <input
            type="text"
            value={
              localFilters.search
            }
            placeholder="Search quotation number, customer or mobile..."
            onChange={(
              event
            ) =>
              updateFilter(
                "search",
                event.target
                  .value
              )
            }
            onKeyDown={
              handleSearchKeyDown
            }
          />

          {localFilters.search && (
            <button
              type="button"
              onClick={
                clearSearch
              }
              title="Clear search"
            >
              <FiX />
            </button>
          )}
        </div>

        {renderSelect({
          key:
            "documentStatus",

          label:
            "Status",

          options:
            documentStatusOptions,

          placeholder:
            "All Statuses",
        })}

        {renderSelect({
          key:
            "conversionStatus",

          label:
            "Conversion",

          options:
            conversionStatusOptions,

          placeholder:
            "All Conversion Statuses",
        })}

        {renderSelect({
          key:
            "salesChannel",

          label:
            "Sales Channel",

          options:
            salesChannelOptions,

          placeholder:
            "All Channels",
        })}

        <div className="quotation-filter-actions">
          <button
            type="button"
            className={[
              "quotation-filter-button",
              "quotation-filter-button-secondary",
              showAdvancedFilters
                ? "quotation-filter-button-active"
                : "",
            ]
              .filter(Boolean)
              .join(" ")}
            onClick={() =>
              setShowAdvancedFilters(
                (
                  current
                ) =>
                  !current
              )
            }
          >
            <FiFilter />

            <span>
              More Filters
            </span>

            {activeFilterCount >
              0 && (
              <strong>
                {
                  activeFilterCount
                }
              </strong>
            )}
          </button>

          <button
            type="button"
            className="quotation-filter-button quotation-filter-button-primary"
            onClick={
              handleApply
            }
            disabled={
              loading
            }
          >
            {loading ? (
              <FiRefreshCw className="quotation-filter-spin" />
            ) : (
              <FiSearch />
            )}

            <span>
              {loading
                ? "Loading..."
                : "Apply"}
            </span>
          </button>

          <button
            type="button"
            className="quotation-filter-button quotation-filter-button-reset"
            onClick={
              handleReset
            }
            disabled={
              loading
            }
          >
            <FiRefreshCw />

            <span>
              Reset
            </span>
          </button>
        </div>
      </div>

      {showAdvancedFilters && (
        <div className="quotation-filter-advanced">
          <div className="quotation-filter-field">
            <label htmlFor="quotation-filter-from-date">
              Quotation From
            </label>

            <div className="quotation-filter-date">
              <FiCalendar />

              <input
                id="quotation-filter-from-date"
                type="date"
                value={
                  localFilters.fromDate
                }
                onChange={(
                  event
                ) =>
                  updateFilter(
                    "fromDate",
                    event.target
                      .value
                  )
                }
              />
            </div>
          </div>

          <div className="quotation-filter-field">
            <label htmlFor="quotation-filter-to-date">
              Quotation To
            </label>

            <div className="quotation-filter-date">
              <FiCalendar />

              <input
                id="quotation-filter-to-date"
                type="date"
                value={
                  localFilters.toDate
                }
                min={
                  localFilters.fromDate ||
                  undefined
                }
                onChange={(
                  event
                ) =>
                  updateFilter(
                    "toDate",
                    event.target
                      .value
                  )
                }
              />
            </div>
          </div>

          <div className="quotation-filter-field">
            <label htmlFor="quotation-filter-valid-until-from">
              Valid Until From
            </label>

            <div className="quotation-filter-date">
              <FiCalendar />

              <input
                id="quotation-filter-valid-until-from"
                type="date"
                value={
                  localFilters.validUntilFrom
                }
                onChange={(
                  event
                ) =>
                  updateFilter(
                    "validUntilFrom",
                    event.target
                      .value
                  )
                }
              />
            </div>
          </div>

          <div className="quotation-filter-field">
            <label htmlFor="quotation-filter-valid-until-to">
              Valid Until To
            </label>

            <div className="quotation-filter-date">
              <FiCalendar />

              <input
                id="quotation-filter-valid-until-to"
                type="date"
                value={
                  localFilters.validUntilTo
                }
                min={
                  localFilters.validUntilFrom ||
                  undefined
                }
                onChange={(
                  event
                ) =>
                  updateFilter(
                    "validUntilTo",
                    event.target
                      .value
                  )
                }
              />
            </div>
          </div>

          {sourceTypeOptions.length >
            0 &&
            renderSelect({
              key:
                "sourceType",

              label:
                "Source Type",

              options:
                sourceTypeOptions,

              placeholder:
                "All Source Types",
            })}

          {normalizedCustomerOptions.length >
            0 &&
            renderSelect({
              key:
                "customerId",

              label:
                "Customer",

              options:
                normalizedCustomerOptions,

              placeholder:
                "All Customers",
            })}

          {normalizedSalespersonOptions.length >
            0 &&
            renderSelect({
              key:
                "salespersonId",

              label:
                "Salesperson",

              options:
                normalizedSalespersonOptions,

              placeholder:
                "All Salespersons",
            })}

          {normalizedBranchOptions.length >
            0 &&
            renderSelect({
              key:
                "branchId",

              label:
                "Branch",

              options:
                normalizedBranchOptions,

              placeholder:
                "All Branches",
            })}

          {normalizedFirmOptions.length >
            0 &&
            renderSelect({
              key:
                "firmId",

              label:
                "Firm",

              options:
                normalizedFirmOptions,

              placeholder:
                "All Firms",
            })}

          {normalizedWarehouseOptions.length >
            0 &&
            renderSelect({
              key:
                "warehouseId",

              label:
                "Warehouse",

              options:
                normalizedWarehouseOptions,

              placeholder:
                "All Warehouses",
            })}

          {renderSelect({
            key:
              "sortBy",

            label:
              "Sort By",

            options:
              sortByOptions,

            placeholder:
              "Default Sorting",
          })}

          {renderSelect({
            key:
              "sortOrder",

            label:
              "Sort Order",

            options:
              sortOrderOptions,

            placeholder:
              "Select Order",
          })}
        </div>
      )}
    </div>
  );
};

export default QuotationFilter;